/**
 * Custom hook for managing conversation context state and data fetching in the modal.
 *
 * Manages the full lifecycle of a conversation context view:
 * - Fetches initial context (preceding + following messages) around a selected message
 * - Supports bidirectional pagination ("load more above" / "load more below")
 * - Deduplicates messages when paginated results overlap with existing state
 * - Resets all state when the modal closes
 *
 * @param params - {@link UseConversationContextModalParams}
 * @returns Conversation state, loading indicators, error state, and pagination handlers
 */

import type { ContentCardItem, ContentSearchItem } from '@content-search/domain';
import { useConversationContext } from '@content-search/hooks';
import { useEffect, useState, useCallback, useMemo } from 'react';

import { hasValidMetadata } from './helpers';

export interface UseConversationContextModalParams {
  isOpen: boolean;
  communityId: string;
  subjectId: string;
  topicName: string;
  metadata: Record<string, unknown>;
  selectedMessage?: ContentCardItem;
}

export function useConversationContextModal({ isOpen, communityId, subjectId, topicName, metadata, selectedMessage }: UseConversationContextModalParams) {
  const { fetchConversationContext, fetchMoreContext, isLoading, errors, resetErrors } = useConversationContext();

  const [targetMessage, setTargetMessage] = useState<ContentSearchItem | null>(null);
  const [conversationMessages, setConversationMessages] = useState<ContentSearchItem[]>([]);
  const [loadingDirection, setLoadingDirection] = useState<'above' | 'below' | null>(null);
  const [canLoadMore, setCanLoadMore] = useState({ above: true, below: true });
  const [hasScrolledToTarget, setHasScrolledToTarget] = useState(false);

  const memoizedMetadata = useMemo(() => metadata, [metadata]);
  const isMetadataValid = useMemo(() => hasValidMetadata(memoizedMetadata), [memoizedMetadata]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetErrors();
      setTargetMessage(null);
      setConversationMessages([]);
      setCanLoadMore({ above: true, below: true });
      setHasScrolledToTarget(false);
    }
  }, [isOpen, resetErrors]);

  // Fetch conversation context when modal opens
  useEffect(() => {
    if (!isOpen || !isMetadataValid) {
      if (!isMetadataValid && isOpen) {
        setTargetMessage(null);
        setConversationMessages([]);
        setCanLoadMore({ above: false, below: false });
      }

      return;
    }

    // eslint-disable-next-line complexity -- builds selectedAsContentSearchItem from ContentCardItem shape (many field mappings)
    const loadConversationContext = async () => {
      try {
        const context = await fetchConversationContext({
          communityId,
          subjectId,
          topicName,
          metadata: memoizedMetadata,
          messagesBefore: 5,
          messagesAfter: 5,
        });

        if (context) {
          let selectedAsContentSearchItem: ContentSearchItem | null = null;

          if (selectedMessage) {
            const messageSubjectId = selectedMessage.subjectId || selectedMessage.id;

            selectedAsContentSearchItem = {
              id: selectedMessage.id,
              subjectId: messageSubjectId,
              timestamp: selectedMessage.timestamp,
              eventType: selectedMessage.eventType || 'Classification',
              createdAt: selectedMessage.timestamp || new Date().toISOString(),
              communityId,
              topicName: selectedMessage.topicName || selectedMessage.topic || '',
              contentType: selectedMessage.contentType || selectedMessage.type || 'audio',
              isSampled: selectedMessage.isSampled || false,
              classificationResults: { transcript: selectedMessage.text || '' },
              contentMetadata: {
                subjectId: messageSubjectId,
                audioFileUrl: selectedMessage.audioFileUrl || '',
                contentUrl: selectedMessage.contentUrl || '',
                timestamp: selectedMessage.timestamp || '',
                metadata: selectedMessage.metadata || {},
                subjectOwner: {
                  type: selectedMessage.subjectOwner?.type || '',
                  id: selectedMessage.subjectOwner?.userId || '',
                  additionalInfo: { username: selectedMessage.subjectOwner?.username || '' },
                },
                customValues: selectedMessage.customFields || {},
              },
            };

            setTargetMessage(selectedAsContentSearchItem);
          } else {
            const combinedMessages = [...context.precedingMessages, ...context.followingMessages];
            const actualTargetMessage =
              context.targetFound && context.targetSubjectId
                ? combinedMessages.find(message => (message.subjectId || message.id) === context.targetSubjectId) || null
                : null;

            setTargetMessage(actualTargetMessage);
          }

          const reversedPrecedingMessages = [...context.precedingMessages].reverse();
          const chronologicalMessages: ContentSearchItem[] = [
            ...reversedPrecedingMessages,
            ...(selectedAsContentSearchItem ? [selectedAsContentSearchItem] : []),
            ...context.followingMessages,
          ];

          setConversationMessages(chronologicalMessages);
          setCanLoadMore({
            above: context.precedingMessages.length >= 5,
            below: context.followingMessages.length >= 5,
          });
        }
      } catch {
        // Error state is managed by useConversationContext hook;
        // catch prevents unhandled promise rejection.
        setTargetMessage(null);
        setConversationMessages([]);
        setCanLoadMore({ above: false, below: false });
      }
    };

    loadConversationContext();
  }, [isOpen, communityId, subjectId, topicName, memoizedMetadata, isMetadataValid, fetchConversationContext, selectedMessage]);

  // Load more above handler
  // eslint-disable-next-line complexity -- guards + pagination state checks before fetch
  const handleLoadMoreAbove = useCallback(async () => {
    const earliestMessage = conversationMessages[0];
    const messageId = (earliestMessage?.contentMetadata?.subjectId as string) || earliestMessage?.subjectId || earliestMessage?.id;

    if (!conversationMessages.length || loadingDirection || !isMetadataValid || !earliestMessage || !messageId || typeof messageId !== 'string') {
      return;
    }

    setLoadingDirection('above');

    try {
      const context = await fetchMoreContext({
        communityId,
        subjectId: messageId,
        topicName,
        metadata: memoizedMetadata,
        messagesBefore: 5,
        messagesAfter: 0,
      });

      if (context && context.precedingMessages.length > 0) {
        const reversedNewMessages = [...context.precedingMessages].reverse();

        setConversationMessages(prev => {
          const existingIds = new Set(prev.map(m => m.subjectId || m.id));
          const uniqueNewMessages = reversedNewMessages.filter(m => !existingIds.has(m.subjectId || m.id));

          return [...uniqueNewMessages, ...prev];
        });
        setCanLoadMore(prev => ({ ...prev, above: context.precedingMessages.length >= 5 }));
      } else {
        setCanLoadMore(prev => ({ ...prev, above: false }));
      }
    } finally {
      setLoadingDirection(null);
    }
  }, [communityId, conversationMessages, topicName, memoizedMetadata, isMetadataValid, fetchMoreContext, loadingDirection]);

  // Load more below handler
  // eslint-disable-next-line complexity -- guards + pagination state checks before fetch
  const handleLoadMoreBelow = useCallback(async () => {
    const bottomMostMessage = conversationMessages[conversationMessages.length - 1];

    if (!conversationMessages.length || loadingDirection || !isMetadataValid) {
      return;
    }

    const messageId = (bottomMostMessage?.contentMetadata?.subjectId as string) || bottomMostMessage?.subjectId || bottomMostMessage?.id;

    setLoadingDirection('below');

    try {
      if (!messageId || typeof messageId !== 'string') {
        return;
      }

      const context = await fetchMoreContext({
        communityId,
        subjectId: messageId,
        topicName,
        metadata: memoizedMetadata,
        messagesBefore: 0,
        messagesAfter: 5,
      });

      if (context && context.followingMessages.length > 0) {
        setConversationMessages(prev => {
          const existingIds = new Set(prev.map(m => m.subjectId || m.id));
          const uniqueNewMessages = context.followingMessages.filter(m => !existingIds.has(m.subjectId || m.id));

          return [...prev, ...uniqueNewMessages];
        });
        setCanLoadMore(prev => ({ ...prev, below: context.followingMessages.length >= 5 }));
      } else {
        setCanLoadMore(prev => ({ ...prev, below: false }));
      }
    } finally {
      setLoadingDirection(null);
    }
  }, [communityId, conversationMessages, topicName, memoizedMetadata, isMetadataValid, fetchMoreContext, loadingDirection]);

  return {
    isLoading,
    errors,
    resetErrors,
    isMetadataValid,
    memoizedMetadata,
    targetMessage,
    conversationMessages,
    loadingDirection,
    canLoadMore,
    hasScrolledToTarget,
    setHasScrolledToTarget,
    handleLoadMoreAbove,
    handleLoadMoreBelow,
  };
}
